import React, { useState, useMemo, useEffect, useContext, useCallback, useRef } from 'react';
import { useUserPrefs } from '../shared/hooks/useUserPrefs';
import {
    ActionDependant,
    GlobalDataContext,
    TemplateContext,
    evaluateTemplateValue,
    ReactiveJsonRoot,
    mergeComponentCollections,
    dataLocationToPath,
} from "@ea-lab/reactive-json";
import { formatDecoupledError } from '../../notices';
import { initials } from '../shared/ui/InitialsAvatar';

// ── Saisie d'une durée en heures + minutes ───────────────────────────────────
// Le back stocke une durée en HEURES DÉCIMALES (decimal(10,2)) : 1h30 = 1.50,
// 20 min = 0.33. On saisit pourtant deux champs (HH / MM) plus lisibles, et on
// convertit dans les deux sens. Le scale 2 rend l'aller-retour STABLE pour toute
// minute 0-59 (20 min → 0.33 → round(0.33×60)=20).
//
// Sémantique du VIDE (elle porte le modèle de RAF) :
//   • les DEUX sous-champs vides  → valeur '' = « non renseigné »
//       - Passé : le bouton Ajouter reste désactivé (rien à logger) ;
//       - Reste à faire : « non déclaré » → le RAF de la tâche reste DÉRIVÉ.
//   • un seul rempli → l'autre vaut 0 (les minutes vides d'un « 1h » = 1h00).
//   • 0 explicitement saisi (heures « 0 ») → valeur 0 = déclaration « il ne reste
//       rien », distincte du vide. Le champ heures porte ce signal rempli/vide :
//       vide = placeholder « 00 » ; « 0 » = zéro affiché.
const decToHM = (v) => {
    if (v === '' || v === null || v === undefined) return { h: '', m: '' };
    const dec = parseFloat(v);
    if (Number.isNaN(dec)) return { h: '', m: '' };
    let h = Math.floor(dec);
    let m = Math.round((dec - h) * 60);
    if (m === 60) { h += 1; m = 0; }
    // Minutes à 0 → placeholder « 00 » ; heures gardent « 0 » pour signaler le rempli.
    return { h: String(h), m: m === 0 ? '' : String(m) };
};

const hmToDec = (h, m) => {
    const hEmpty = h === '' || h === null || h === undefined;
    const mEmpty = m === '' || m === null || m === undefined;
    if (hEmpty && mEmpty) return '';
    const hi = parseInt(h || '0', 10) || 0;
    const mi = Math.min(59, Math.max(0, parseInt(m || '0', 10) || 0));
    return Math.round((hi + mi / 60) * 100) / 100;
};

// Heures décimales -> libellé « 1h30 » / « 2h » / « 0h30 » (affichage lecture
// seule : estimation, temps passé, RAF). Vide/illisible -> « — ».
export const decimalToHMLabel = (v) => {
    if (v === '' || v === null || v === undefined) return '—';
    const { h, m } = decToHM(v);
    if (h === '' && m === '') return '—';
    const hi = h === '' ? 0 : parseInt(h, 10);
    const mi = m === '' ? 0 : parseInt(m, 10);
    if (mi === 0) return `${hi}h`;
    return `${hi}h${String(mi).padStart(2, '0')}`;
};

// Présentation pure : value = heures décimales (ou ''), onChange rend le décimal.
// Réutilisé par le champ reactive-json (formulaire d'ajout) ET l'édition inline.
export const HoursMinutesInput = ({ value, onChange, label = '', compact = false, error = false }) => {
    const [h, setH] = useState(() => decToHM(value).h);
    const [m, setM] = useState(() => decToHM(value).m);
    // Ne re-synchroniser QUE sur un changement EXTERNE (reset du formulaire après
    // save, réouverture d'une ligne) — jamais sur notre propre écriture, pour ne
    // pas hoqueter en cours de frappe.
    const lastEmitted = useRef(value);
    useEffect(() => {
        if (value === lastEmitted.current) return;
        const next = decToHM(value);
        setH(next.h); setM(next.m);
        lastEmitted.current = value;
    }, [value]);

    const emit = (nh, nm) => {
        const dec = hmToDec(nh, nm);
        lastEmitted.current = dec;
        onChange(dec);
    };
    const onH = (e) => { const v = e.target.value.replace(/\D/g, ''); setH(v); emit(v, m); };
    const onM = (e) => {
        let v = e.target.value.replace(/\D/g, '');
        if (v !== '' && parseInt(v, 10) > 59) v = '59';
        setM(v); emit(h, v);
    };

    const box = `input text-center tabular-nums ${compact ? 'w-12' : 'w-full'}${error ? ' input-error' : ''}`;
    return (
        <div className="flex items-center gap-1.5">
            <input type="text" inputMode="numeric" value={h} onChange={onH}
                onFocus={(e) => e.target.select()} placeholder="00" maxLength={3}
                aria-label={`${label} — heures`} className={box} />
            <span className="text-slate-400 text-xs font-medium select-none">h</span>
            <input type="text" inputMode="numeric" value={m} onChange={onM}
                onFocus={(e) => e.target.select()} placeholder="00" maxLength={2}
                aria-label={`${label} — minutes`} className={box} />
            <span className="text-slate-400 text-xs font-medium select-none">min</span>
        </div>
    );
};

// Champ reactive-json : lit/écrit une durée décimale à `dataLocation`.
export const HoursMinutesField = ({ props, path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);

    const value = evaluateTemplateValue({ valueToEvaluate: props.dataLocation, globalDataContext, templateContext });
    const label = evaluateTemplateValue({ valueToEvaluate: props.label, globalDataContext, templateContext }) || '';

    const handleChange = (next) => {
        const dataPath = dataLocationToPath({ dataLocation: props.dataLocation, currentPath: path, globalDataContext, templateContext });
        globalDataContext.updateData(next, dataPath);
    };

    return <HoursMinutesInput value={value ?? ''} onChange={handleChange} label={label} />;
};

// Affichage seul d'une durée décimale en « 1h30 » (reactive-json element).
export const HoursMinutesLabel = ({ props }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);
    const value = evaluateTemplateValue({ valueToEvaluate: props.value, globalDataContext, templateContext });
    return <>{decimalToHMLabel(value)}</>;
};

export const TimeLogUserSelect = ({ props, path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);

    const currentValue = String(evaluateTemplateValue({
        valueToEvaluate: props.dataLocation, globalDataContext, templateContext,
    }) ?? '');

    const options = evaluateTemplateValue({
        valueToEvaluate: props.options, globalDataContext, templateContext,
    }) || [];

    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const dropdownRef = useRef(null);
    const searchRef = useRef(null);

    const selected = options.find(u => String(u.value) === currentValue);

    const filtered = useMemo(() => {
        if (!search) return options;
        const q = search.toLowerCase();
        return options.filter(u => (u.label || '').toLowerCase().includes(q));
    }, [options, search]);

    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false); setSearch('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && searchRef.current) searchRef.current.focus();
    }, [isOpen]);

    const handleSelect = (user) => {
        const dataPath = dataLocationToPath({
            dataLocation: props.dataLocation, currentPath: path, globalDataContext, templateContext,
        });
        globalDataContext.updateData(user.value, dataPath);
        setIsOpen(false); setSearch('');
    };

    return (
        <ActionDependant {...props}>
            <div className="relative" ref={dropdownRef}>
                <button
                    type="button"
                    onClick={() => { setIsOpen(o => !o); setSearch(''); }}
                    className="flex items-center gap-2 w-full text-left bg-white border-2 border-slate-300 rounded-md px-3 py-2 text-sm hover:border-iris-400 focus:outline-none focus:ring-2 focus:ring-iris-500 transition-colors"
                >
                    {selected ? (
                        <>
                            <span className="avatar avatar-xs flex-shrink-0">
                                {initials(selected.label || '?')}
                            </span>
                            <span className="truncate flex-1">{selected.label}</span>
                        </>
                    ) : (
                        <span className="text-slate-400 flex-1">Sélectionner...</span>
                    )}
                    <svg className={`h-4 w-4 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {isOpen && (
                    <div className="absolute top-full left-0 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden">
                        {options.length > 5 && (
                            <div className="p-2 border-b border-slate-100">
                                <input
                                    ref={searchRef}
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Rechercher..."
                                    className="w-full text-sm border border-slate-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-iris-500 focus:border-iris-500"
                                />
                            </div>
                        )}
                        <div className="max-h-48 overflow-y-auto">
                            {filtered.length > 0 ? filtered.map(user => (
                                <button
                                    key={user.value}
                                    type="button"
                                    onClick={() => handleSelect(user)}
                                    className={`flex items-center gap-2 w-full text-left px-3 py-2 text-sm transition-colors
                                        ${String(user.value) === currentValue ? 'bg-iris-50 text-iris-700' : 'hover:bg-slate-50 text-slate-800'}
                                    `}
                                >
                                    <span className={`avatar avatar-xs flex-shrink-0 ${String(user.value) === currentValue ? 'bg-iris-200 text-iris-800' : 'bg-slate-100 text-slate-600'}`}>
                                        {initials(user.label || '?')}
                                    </span>
                                    <span className="truncate">{user.label}</span>
                                    {String(user.value) === currentValue && (
                                        <svg className="h-4 w-4 text-iris-600 ml-auto flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                    )}
                                </button>
                            )) : (
                                <div className="px-3 py-3 text-sm text-slate-400 italic text-center">Aucun utilisateur trouvé</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </ActionDependant>
    );
};

export const DatePickerField = ({ props, path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);
    const inputRef = useRef(null);

    const currentValue = evaluateTemplateValue({
        valueToEvaluate: props.dataLocation, globalDataContext, templateContext,
    }) || '';

    const handleChange = (e) => {
        const dataPath = dataLocationToPath({
            dataLocation: props.dataLocation, currentPath: path, globalDataContext, templateContext,
        });
        globalDataContext.updateData(e.target.value, dataPath);
    };

    return (
        <ActionDependant {...props}>
            <div className="relative">
                <input
                    ref={inputRef}
                    type="date"
                    value={currentValue}
                    onChange={handleChange}
                    onClick={() => inputRef.current?.showPicker?.()}
                    className={props.attributes?.class || "block w-full text-sm border-2 border-slate-300 rounded-md focus:ring-iris-500 focus:border-iris-500 px-3 py-2 cursor-pointer"}
                />
            </div>
        </ActionDependant>
    );
};

export const TimeLogAddButton = ({ props, path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);

    const val = (loc) => evaluateTemplateValue({ valueToEvaluate: loc, globalDataContext, templateContext }) || '';

    const date = val(props.dateField);
    const user = val(props.userField);
    const timeSpent = val(props.timeSpentField);
    // Neither the "Reste à faire" (timeLeft) nor the description are mandatory:
    // the task RAF is derived (estimation − temps passé) and field_log_description
    // is not required in Drupal. A log only needs a date, a user and a time spent.
    const isValid = !!(date && user && timeSpent);

    const handleClick = () => {
        if (!isValid) return;
        const dataPath = dataLocationToPath({
            dataLocation: props.triggerField, currentPath: path, globalDataContext, templateContext,
        });
        globalDataContext.updateData(true, dataPath);
    };

    return (
        <button
            onClick={handleClick}
            disabled={!isValid}
            className="btn btn-primary btn-block"
        >
            Ajouter
        </button>
    );
};

export const TimeLogRow = ({ props, path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);

    const val = (loc) => evaluateTemplateValue({ valueToEvaluate: loc, globalDataContext, templateContext });

    // La forme de la réponse decoupled dépend du DROIT D'ÉCRITURE, ligne par ligne :
    // AccessAwareSyncableSubscriber retire submission_url/delete_url selon
    // $entity->access('update'), et RequestEntity rend alors les champs À PLAT, sans
    // enveloppe `data`. Une même collection mélange donc les deux formes. Ne lire que
    // `.data.` affichait des lignes fantômes (date «—», utilisateur et description
    // vides) à tout compte non-admin — et un tableau MIXTE aux comptes partiellement
    // habilités. Même normalisation que menuTreeApi.js et DocSpace.
    const field = (k) => val(`~.data.${k}`) ?? val(`~.${k}`);

    const date = field('date') || '';
    const user = field('user') || '';
    const description = field('description') || '';
    const timeSpent = field('timeSpent') ?? '';
    const timeLeft = field('timeLeft') ?? '';
    const taskId = field('taskId') || val('~~.taskId') || '';
    const projectId = field('projectId') || val('~~.projectId') || '';
    // Absents = le serveur refuse l'écriture sur CETTE ligne : c'est son verdict
    // d'accès, on s'en sert pour n'offrir que les actions réellement possibles.
    const deleteUrl = val('~.delete_url') || '';
    const submissionUrl = val('~.submission_url') || '';
    const collectionUrl = val('~~.timeLogCollectionUrl') || '';
    const userOptions = val('~~.userOptions') || [];

    const userName = useMemo(() => {
        const opt = userOptions.find(u => String(u.value) === String(user));
        return opt ? opt.label : user;
    }, [userOptions, user]);

    const formatDateFR = (iso) => {
        if (!iso) return '—';
        const dateOnly = iso.includes('T') ? iso.split('T')[0] : iso;
        const parts = dateOnly.split('-');
        if (parts.length !== 3) return iso;
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    };

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    // Per-field validation errors from the last save ({ apiKey: { label, message } }),
    // used to mark the individual inputs. Cleared when (re)opening the editor.
    const [fieldErrors, setFieldErrors] = useState(null);
    const [draft, setDraft] = useState({});

    const startEdit = () => {
        setDraft({ date, user, description, timeSpent: timeSpent ?? '', timeLeft: timeLeft ?? '' });
        setSaveError('');
        setFieldErrors(null);
        setIsEditing(true);
    };

    const cancelEdit = () => {
        setIsEditing(false);
        setSaveError('');
        setFieldErrors(null);
        setDraft({});
    };

    const refreshCollection = useCallback(async () => {
        if (!collectionUrl) return;
        const resp = await fetch(collectionUrl, { credentials: 'include' });
        if (!resp.ok) return;
        const json = await resp.json();
        const dp = dataLocationToPath({ dataLocation: '~~.timeLogs', currentPath: path, globalDataContext, templateContext });
        globalDataContext.updateData(json, dp);
    }, [collectionUrl, path, globalDataContext, templateContext]);

    const commitEdit = async () => {
        if (!submissionUrl) { console.error('No submission_url for time log'); return; }
        setIsSaving(true);
        setSaveError('');
        setFieldErrors(null);
        try {
            // Ne poster QUE les champs réellement édités ici. `title` était envoyé
            // en dur à « Time log » : le save decoupled étant un PATCH sur les seules
            // clés reçues, tout titre existant (« Log 2026-03-02 — jean.martin »)
            // était écrasé à chaque modification, même pour corriger une virgule.
            // Le titre n'est ni affiché ni éditable ici : on n'a rien à en dire.
            const syncableObject = {
                submission_url: submissionUrl,
                data: {
                    date: draft.date,
                    user: draft.user,
                    description: draft.description,
                    timeSpent: draft.timeSpent,
                    timeLeft: draft.timeLeft,
                    taskId,
                    projectId,
                },
                status: null,
            };
            const resp = await fetch(submissionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(syncableObject),
                credentials: 'include',
            });
            const responseData = await resp.json().catch(() => null);
            if (!resp.ok || responseData?.status?.type === 'error') {
                const { message, fieldErrors: fe } = formatDecoupledError(responseData, resp.status);
                setSaveError(message);
                setFieldErrors(fe);
                return;
            }
            await refreshCollection();
            // Tell the parent fiche to refresh its Charge & temps display (the
            // server recomputed task.field_time_spent / field_raf on this save).
            window.dispatchEvent(new CustomEvent('task:entity-updated', { detail: { type: 'task', id: taskId, projectId } }));
            setIsEditing(false);
            setDraft({});
        } catch (e) {
            console.error('Time log save failed:', e);
            setSaveError(e.message || 'Erreur inattendue');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteUrl) return;
        if (!window.confirm('Supprimer cette entrée de temps ?')) return;
        try {
            const resp = await fetch(deleteUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: '{}',
                credentials: 'include',
            });
            const responseData = await resp.json().catch(() => null);
            if (!resp.ok || responseData?.status?.type === 'error') {
                const msg = responseData?.status?.message || `Erreur de suppression (${resp.status})`;
                console.error('Delete failed:', msg);
                return;
            }
            await refreshCollection();
            window.dispatchEvent(new CustomEvent('task:entity-updated', { detail: { type: 'task', id: taskId, projectId } }));
        } catch (e) {
            console.error('Delete failed:', e);
        }
    };

    const editUserOptions = useMemo(() =>
        userOptions.map(u => ({ value: String(u.value), label: u.label })),
    [userOptions]);

    const editInputCls = "block w-full text-sm border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-iris-500 focus:border-iris-500 px-3 py-2 bg-white shadow-sm hover:border-slate-400 transition-colors";

    if (isEditing) {
        return (
            <>
                <tr className="bg-iris-50/60">
                    <td className="px-5 py-3.5 whitespace-nowrap">
                        <input type="date" value={draft.date} onChange={(e) => setDraft(d => ({ ...d, date: e.target.value }))} className={`input cursor-pointer${fieldErrors?.date ? ' input-error' : ''}`} />
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                        <select value={draft.user} onChange={(e) => setDraft(d => ({ ...d, user: e.target.value }))} className={`select${fieldErrors?.user ? ' select-error' : ''}`}>
                            <option value="">Sélectionner...</option>
                            {editUserOptions.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                        </select>
                    </td>
                    <td className="px-5 py-3.5">
                        <input type="text" value={draft.description} onChange={(e) => setDraft(d => ({ ...d, description: e.target.value }))} className={`input${fieldErrors?.description ? ' input-error' : ''}`} placeholder="Description..." />
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                        <HoursMinutesInput
                            compact
                            label="Temps passé"
                            value={draft.timeSpent}
                            onChange={(v) => setDraft(d => ({ ...d, timeSpent: v }))}
                            error={!!fieldErrors?.timeSpent}
                        />
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                        <HoursMinutesInput
                            compact
                            label="Reste à faire"
                            value={draft.timeLeft}
                            onChange={(v) => setDraft(d => ({ ...d, timeLeft: v }))}
                            error={!!fieldErrors?.timeLeft}
                        />
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={commitEdit}
                                disabled={isSaving}
                                className="btn btn-primary btn-sm"
                                title="Sauvegarder"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                {isSaving ? '...' : 'OK'}
                            </button>
                            <button
                                onClick={cancelEdit}
                                disabled={isSaving}
                                className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 bg-white border border-slate-300 hover:bg-slate-50 transition-colors shadow-sm"
                                title="Annuler"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                        </div>
                    </td>
                </tr>
                {saveError && (
                    <tr className="bg-red-50">
                        <td colSpan="6" className="px-5 py-2 text-xs text-red-600 font-medium whitespace-pre-line">{saveError}</td>
                    </tr>
                )}
            </>
        );
    }

    return (
        <tr className="hover:bg-iris-50/30 transition-colors group">
            <td className="px-5 py-4 whitespace-nowrap text-[13px] text-slate-700 font-medium tabular-nums">{formatDateFR(date)}</td>
            <td className="px-5 py-4 whitespace-nowrap text-[13px] text-slate-900 font-semibold">{userName}</td>
            <td className="px-5 py-4 text-[13px] text-slate-600 leading-relaxed">{description}</td>
            <td className="px-5 py-4 whitespace-nowrap text-[13px] text-right font-bold text-slate-900 tabular-nums">{timeSpent !== '' ? decimalToHMLabel(timeSpent) : ''}</td>
            {/* Tester la PRÉSENCE, pas la vérité : un reste déclaré à 0 signifie
                « il ne reste rien » — c'est la déclaration la plus forte du modèle,
                et elle fait foi pour le RAF de la tâche. La rendre « — » (inconnu)
                inverserait son sens. Même piège que les champs priorité/complexité.
                decimalToHMLabel(0) = « 0h » (déclaré) ; le vide reste « — ». */}
            <td className="px-5 py-4 whitespace-nowrap text-[13px] text-right text-slate-400 tabular-nums">{timeLeft !== '' && timeLeft !== null && timeLeft !== undefined ? decimalToHMLabel(timeLeft) : '—'}</td>
            <td className="px-5 py-4 whitespace-nowrap text-right">
                {/* N'offrir que ce que le serveur autorise : l'absence de submission_url /
                    delete_url EST son verdict d'accès sur cette ligne. Avant, les deux
                    boutons s'affichaient pour tout le monde et handleDelete sortait en
                    silence faute d'URL — une action proposée qui ne faisait rien. */}
                <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {submissionUrl && (
                        <button onClick={startEdit} className="p-1.5 rounded-md hover:bg-iris-100 text-slate-400 hover:text-iris-600 transition-colors" title="Modifier">
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                    )}
                    {deleteUrl && (
                        <button onClick={handleDelete} className="p-1.5 rounded-md hover:bg-red-100 text-slate-400 hover:text-red-500 transition-colors" title="Supprimer">
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                        </button>
                    )}
                </div>
            </td>
        </tr>
    );
};

/**
 * Invisible notifier that watches the time-log collection length and fires a
 * `task:entity-updated` window event whenever it changes. This covers the
 * CREATE path (whose POST happens inside the YAML DataSync, with no JS hook) and
 * the delete path; the parent TaskEdit listens and refreshes its Charge & temps
 * display. Edit (same length) is covered by the explicit dispatch in commitEdit.
 */
export const TimeLogChangeNotifier = ({ path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);
    const logs = evaluateTemplateValue({ valueToEvaluate: '~~.timeLogs', globalDataContext, templateContext });
    const taskId = evaluateTemplateValue({ valueToEvaluate: '~~.taskId', globalDataContext, templateContext });
    const count = Array.isArray(logs) ? logs.length : 0;
    const prevCount = useRef(null);
    useEffect(() => {
        if (prevCount.current !== null && count !== prevCount.current) {
            window.dispatchEvent(new CustomEvent('task:entity-updated', { detail: { type: 'task', id: taskId } }));
        }
        prevCount.current = count;
    }, [count, taskId]);
    return null;
};

const TIME_LOG_PLUGINS = mergeComponentCollections([{
    element: { TimeLogUserSelect, DatePickerField, TimeLogAddButton, TimeLogRow, TimeLogChangeNotifier, HoursMinutesField },
}]);

const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const normalizeDate = (d) => {
    if (!d) return '';
    return typeof d === 'string' && d.includes('T') ? d.split('T')[0] : d;
};

const getDaysBetween = (start, end) => {
    const days = [];
    const current = new Date(start);
    const endDate = new Date(end);
    current.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    while (current <= endDate) {
        days.push(new Date(current));
        current.setDate(current.getDate() + 1);
    }
    return days;
};

const TaskSearchInput = ({ value, onChange, tasks = [], projects = [], placeholder = "Rechercher une tâche..." }) => {
    const [search, setSearch] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);

    // T36: resolve project name + internal flag (project_settings.is_internal, T23).
    const projectMap = useMemo(() => {
        const m = {};
        (projects || []).forEach(p => {
            let internal = false;
            try {
                const s = typeof p.projectSettings === 'string' ? JSON.parse(p.projectSettings || '{}') : (p.projectSettings || {});
                internal = s?.is_internal === true;
            } catch { /* ignore */ }
            m[String(p.id)] = { name: p.name || `Projet ${p.id}`, internal };
        });
        return m;
    }, [projects]);

    const selectedTask = tasks.find(t => t.id === value);

    useEffect(() => {
        if (selectedTask && !search) {
            setSearch(`#${selectedTask.id} ${selectedTask.title}`);
        }
        if (!value && search) {
            setSearch('');
        }
    }, [value, selectedTask]);

    const filteredTasks = useMemo(() => {
        if (!search) return tasks.slice(0, 30);
        const q = search.toLowerCase();
        return tasks.filter(t =>
            String(t.id).includes(q) ||
            (t.title || '').toLowerCase().includes(q) ||
            (t.status || '').toLowerCase().includes(q)
        ).slice(0, 30);
    }, [tasks, search]);

    // T36: group the results by project so the user distinguishes projects (and
    // internal vs client) instead of a flat "#id title" list.
    const groupedTasks = useMemo(() => {
        const groups = new Map();
        filteredTasks.forEach(t => {
            const pid = String(t.projectId ?? '');
            if (!groups.has(pid)) groups.set(pid, []);
            groups.get(pid).push(t);
        });
        return Array.from(groups.entries());
    }, [filteredTasks]);

    return (
        <div className="relative w-full">
            <input
                type="text"
                value={search}
                onChange={(e) => {
                    setSearch(e.target.value);
                    onChange(null);
                    setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                className="block w-full text-sm border-2 border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500 px-3 py-2"
                placeholder={placeholder}
            />
            {showDropdown && filteredTasks.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto left-0">
                    {groupedTasks.map(([pid, tks]) => (
                        <div key={pid || 'none'}>
                            {/* T36: project header + "Interne" badge for internal projects. */}
                            <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 flex items-center gap-2 sticky top-0">
                                <span className="text-xs font-bold text-slate-600 truncate">{projectMap[pid]?.name || 'Projet'}</span>
                                {projectMap[pid]?.internal && (
                                    <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-700 text-white border border-slate-700">Interne</span>
                                )}
                            </div>
                            {tks.map(t => (
                                <div
                                    key={t.id}
                                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer transition-colors border-b border-slate-100 last:border-b-0"
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        onChange(t.id);
                                        setSearch(`#${t.id} ${t.title}`);
                                        setShowDropdown(false);
                                    }}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="text-blue-600 font-mono text-xs font-bold shrink-0">#{t.id}</span>
                                        <span className="text-sm text-slate-900 truncate">{t.title}</span>
                                    </div>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        {t.status && <span className="text-xs text-slate-400">{t.status}</span>}
                                        {t.assignee && <span className="text-xs text-slate-400">— {t.assignee}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                    {filteredTasks.length === 30 && (
                        <div className="px-3 py-2 text-xs text-slate-400 text-center italic">Affinez votre recherche...</div>
                    )}
                </div>
            )}
            {showDropdown && search && filteredTasks.length === 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg left-0">
                    <div className="px-3 py-3 text-sm text-slate-500 italic text-center">Aucune tâche trouvée</div>
                </div>
            )}
        </div>
    );
};

export const Timesheet = ({ props }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);

    const evaluatedProjects = evaluateTemplateValue({ valueToEvaluate: props.projects, globalDataContext, templateContext });
    const evaluatedIssues = evaluateTemplateValue({ valueToEvaluate: props.issues, globalDataContext, templateContext });
    const evaluatedUsers = evaluateTemplateValue({ valueToEvaluate: props.users, globalDataContext, templateContext });
    const evaluatedLogs = evaluateTemplateValue({ valueToEvaluate: props.logs, globalDataContext, templateContext });
    const evaluatedTitle = evaluateTemplateValue({ valueToEvaluate: props.title, globalDataContext, templateContext });
    const evaluatedUserInfo = evaluateTemplateValue({ valueToEvaluate: props.userInfo, globalDataContext, templateContext });

    const resolveArray = (evaluated, prop) => {
        if (Array.isArray(evaluated)) return evaluated;
        if (Array.isArray(prop)) return prop;
        return [];
    };

    const projects = resolveArray(evaluatedProjects, props.projects);
    const rawIssues = resolveArray(evaluatedIssues, props.issues);
    const users = resolveArray(evaluatedUsers, props.users);
    const title = evaluatedTitle || props.title || "Saisie des temps";

    // Normalize issues: tasksProcessor returns syncable objects { data: { id, title, ... } }
    const issues = useMemo(() => {
        return rawIssues.map(item => {
            if (item?.data && typeof item.data === 'object' && item.data.id != null) {
                return item.data;
            }
            return item;
        });
    }, [rawIssues]);

    const [logs, setLogs] = useState(() => {
        if (Array.isArray(evaluatedLogs)) return evaluatedLogs;
        if (Array.isArray(props.logs)) return props.logs;
        return [];
    });

    useEffect(() => {
        if (Array.isArray(evaluatedLogs)) {
            setLogs(evaluatedLogs);
        }
    }, [evaluatedLogs]);

    const [period, setPeriod] = useState(() => {
        const now = new Date();
        return {
            start: new Date(now.getFullYear(), now.getMonth(), 1),
            end: new Date(now.getFullYear(), now.getMonth() + 1, 0)
        };
    });

    const daysInPeriod = useMemo(() => {
        if (!period.start || !period.end) return [];
        return getDaysBetween(period.start, period.end);
    }, [period.start, period.end]);

    // --- User filter (display name) + derived user ID for data comparisons ---
    const [currentUser, setCurrentUser] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [selectedTaskId, setSelectedTaskId] = useState(null);
    const [hideWeekends, setHideWeekends] = useState(true);
    // T33: hide the per-ticket detail rows (keep the daily totals), persisted (T31).
    const [hideTaskRows, setHideTaskRows] = useUserPrefs('timesheetHideTaskRows', false);
    const [userDropdownOpen, setUserDropdownOpen] = useState(false);
    const [userSearch, setUserSearch] = useState('');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const userDropdownRef = useRef(null);
    const userSearchInputRef = useRef(null);
    const calendarPanelRef = useRef(null);

    useEffect(() => {
        if (!userDropdownOpen) return;
        const handleClickOutside = (e) => {
            if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
                setUserDropdownOpen(false);
                setUserSearch('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [userDropdownOpen]);

    useEffect(() => {
        if (userDropdownOpen && userSearchInputRef.current) {
            userSearchInputRef.current.focus();
        }
    }, [userDropdownOpen]);

    const filteredUserList = useMemo(() => {
        if (!userSearch) return users;
        const q = userSearch.toLowerCase();
        return users.filter(u => (u.name || '').toLowerCase().includes(q));
    }, [users, userSearch]);

    const currentUserObj = useMemo(() => {
        return users.find(u => u.name === currentUser) || null;
    }, [users, currentUser]);

    const currentUserId = currentUserObj?.id ?? null;

    const userInfo = (evaluatedUserInfo && typeof evaluatedUserInfo === 'object' && evaluatedUserInfo.uid)
        ? evaluatedUserInfo
        : null;

    // --- Normalize logs: handles both raw Syncable Objects and flat objects ---
    const normalizedLogs = useMemo(() => {
        if (!Array.isArray(logs)) return [];
        return logs.map(item => {
            if (item?.data && typeof item.data === 'object') {
                const d = item.data;
                return {
                    id: item.id ?? d.id,
                    date: normalizeDate(d.date),
                    user: String(d.user ?? ''),
                    description: d.description || '',
                    timeSpent: parseFloat(d.timeSpent) || 0,
                    timeLeft: parseFloat(d.timeLeft) || 0,
                    taskId: d.taskId != null ? String(d.taskId) : null,
                    projectId: d.projectId != null ? String(d.projectId) : null,
                };
            }
            return {
                ...item,
                date: normalizeDate(item.date),
                user: String(item.user ?? ''),
                timeSpent: parseFloat(item.timeSpent) || 0,
                timeLeft: parseFloat(item.timeLeft) || 0,
                taskId: item.taskId != null ? String(item.taskId) : null,
                projectId: item.projectId != null ? String(item.projectId) : null,
            };
        });
    }, [logs]);

    const userInitFromInfo = useRef(false);

    useEffect(() => {
        if (users.length === 0) return;

        if (userInfo?.uid && !userInitFromInfo.current) {
            const match = users.find(u => String(u.id) === String(userInfo.uid));
            if (match) {
                setCurrentUser(match.name);
                userInitFromInfo.current = true;
                return;
            }
        }

        if (!currentUser && !userInitFromInfo.current) {
            const userIdsWithLogs = new Set(normalizedLogs.map(l => l.user).filter(Boolean));
            const defaultUser = users.find(u => userIdsWithLogs.has(String(u.id)));
            setCurrentUser(defaultUser?.name || users[0]?.name || '');
        }
    }, [users, currentUser, normalizedLogs, userInfo]);

    // --- Grid data ---
    const uid = String(currentUserId ?? '');

    const logsMap = useMemo(() => {
        const map = {};
        if (!uid) return map;
        normalizedLogs.filter(l => l.user === uid).forEach(log => {
            const key = `${log.taskId}-${log.date}`;
            map[key] = (map[key] || 0) + log.timeSpent;
        });
        return map;
    }, [normalizedLogs, uid]);

    // T34: per-cell tooltip listing each log of that ticket/day ("2h — réunion / 3h — dev").
    const logsTooltipMap = useMemo(() => {
        const map = {};
        if (!uid) return map;
        normalizedLogs.filter(l => l.user === uid).forEach(log => {
            const key = `${log.taskId}-${log.date}`;
            const line = `${log.timeSpent}h${log.description ? ' — ' + log.description : ''}`;
            map[key] = map[key] ? `${map[key]} / ${line}` : line;
        });
        return map;
    }, [normalizedLogs, uid]);

    const dailyTotals = useMemo(() => {
        const totals = {};
        if (!uid) return totals;
        daysInPeriod.forEach(day => {
            const dateStr = formatDate(day);
            const dailySum = normalizedLogs
                .filter(l => l.user === uid && l.date === dateStr)
                .reduce((sum, log) => sum + log.timeSpent, 0);
            totals[dateStr] = dailySum;
        });
        return totals;
    }, [normalizedLogs, uid, daysInPeriod]);

    const periodTotal = useMemo(() =>
        Object.values(dailyTotals).reduce((a, b) => a + b, 0),
    [dailyTotals]);

    // Sommes par projet : total de la période (colonne de gauche) et total par jour (ligne projet).
    const projectTotals = useMemo(() => {
        const totals = {};
        if (!uid) return totals;
        const projectByTask = {};
        issues.forEach(i => { projectByTask[String(i.id)] = String(i.projectId); });
        const periodDates = new Set(daysInPeriod.map(day => formatDate(day)));
        normalizedLogs.forEach(log => {
            if (log.user !== uid || !log.taskId || !periodDates.has(log.date)) return;
            const pid = projectByTask[String(log.taskId)];
            if (!pid) return;
            const entry = totals[pid] || (totals[pid] = { total: 0, byDate: {} });
            entry.total += log.timeSpent;
            entry.byDate[log.date] = (entry.byDate[log.date] || 0) + log.timeSpent;
        });
        return totals;
    }, [normalizedLogs, uid, issues, daysInPeriod]);

    // Total par ticket sur la période (colonne de gauche de la ligne du ticket).
    const ticketTotals = useMemo(() => {
        const totals = {};
        if (!uid) return totals;
        const periodDates = new Set(daysInPeriod.map(day => formatDate(day)));
        normalizedLogs.forEach(log => {
            if (log.user !== uid || !log.taskId || !periodDates.has(log.date)) return;
            const tid = String(log.taskId);
            totals[tid] = (totals[tid] || 0) + log.timeSpent;
        });
        return totals;
    }, [normalizedLogs, uid, daysInPeriod]);

    const relevantTaskIds = useMemo(() => {
        const ids = new Set();
        if (!uid) return ids;
        const startStr = formatDate(period.start);
        const endStr = formatDate(period.end);
        normalizedLogs.forEach(log => {
            if (log.user === uid && log.taskId && log.date >= startStr && log.date <= endStr) {
                ids.add(log.taskId);
            }
        });
        return ids;
    }, [normalizedLogs, uid, period]);

    const filteredIssues = useMemo(() => {
        return issues.filter(i => relevantTaskIds.has(String(i.id)));
    }, [issues, relevantTaskIds]);

    const hasLogs = filteredIssues.length > 0;

    const hasWeekendLogs = useMemo(() => {
        if (!uid) return false;
        const startStr = formatDate(period.start);
        const endStr = formatDate(period.end);
        return normalizedLogs.some(log => {
            if (log.user !== uid) return false;
            if (!log.date || log.date < startStr || log.date > endStr) return false;
            const d = new Date(log.date + 'T00:00:00');
            return d.getDay() === 0 || d.getDay() === 6;
        });
    }, [normalizedLogs, uid, period]);

    useEffect(() => {
        setHideWeekends(!hasWeekendLogs);
    }, [hasWeekendLogs]);

    const visibleDays = useMemo(() => {
        if (!hideWeekends) return daysInPeriod;
        return daysInPeriod.filter(day => day.getDay() !== 0 && day.getDay() !== 6);
    }, [daysInPeriod, hideWeekends]);

    const monthSpans = useMemo(() => {
        if (visibleDays.length === 0) return [];
        const spans = [];
        let currentKey = null;
        visibleDays.forEach(day => {
            const key = `${day.getFullYear()}-${day.getMonth()}`;
            if (key !== currentKey) {
                spans.push({
                    key,
                    label: day.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
                    count: 1,
                });
                currentKey = key;
            } else {
                spans[spans.length - 1].count++;
            }
        });
        return spans;
    }, [visibleDays]);

    // --- Modal handlers ---
    const handleCellClick = (date, project, issue) => {
        const dateStr = formatDate(date);
        setModalData({ date: dateStr, project: project.id, taskId: issue.id });
        setSelectedProjectId(project.id);
        setSelectedTaskId(issue.id);
        setIsModalOpen(true);
    };

    const handleOpenNewLog = useCallback(() => {
        setModalData({});
        setSelectedProjectId(null);
        setSelectedTaskId(null);
        setIsModalOpen(true);
    }, []);

    // T29: load only the displayed period server-side (field_log_date range)
    // instead of every log in the database.
    const handleRefreshLogs = useCallback(async () => {
        try {
            const fmt = (d) => {
                const x = new Date(d);
                return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
            };
            const from = fmt(period.start);
            const to = fmt(period.end);
            const ge = encodeURIComponent('>=');
            const le = encodeURIComponent('<=');
            const url = `/decoupled-api/node/time_log/collection?display=decoupled`
                + `&filter[0][f]=field_log_date&filter[0][c]=${ge}&filter[0][v]=${from}`
                + `&filter[1][f]=field_log_date&filter[1][c]=${le}&filter[1][v]=${to}T23:59:59`
                + `&_=${Date.now()}`;
            const resp = await fetch(url, { credentials: 'include' });
            if (!resp.ok) throw new Error(`Fetch failed (${resp.status})`);
            const rawLogs = await resp.json();
            if (Array.isArray(rawLogs)) {
                setLogs(rawLogs);
                globalDataContext.updateData(rawLogs, 'logs');
            }
        } catch (e) {
            console.error('Failed to refresh time logs:', e);
        }
    }, [globalDataContext, period.start, period.end]);

    // T29: (re)fetch the logs whenever the displayed period changes.
    useEffect(() => {
        handleRefreshLogs();
    }, [handleRefreshLogs]);

    const handleModalClose = useCallback(() => {
        setIsModalOpen(false);
        handleRefreshLogs();
    }, [handleRefreshLogs]);

    // --- Period navigation ---
    const handlePrevPeriod = () => {
        const isFullMonth = period.start.getDate() === 1 &&
                           period.end.getDate() === new Date(period.start.getFullYear(), period.start.getMonth() + 1, 0).getDate();
        if (isFullMonth) {
             setPeriod({
                start: new Date(period.start.getFullYear(), period.start.getMonth() - 1, 1),
                end: new Date(period.start.getFullYear(), period.start.getMonth(), 0)
             });
        } else {
             const diff = period.end.getTime() - period.start.getTime();
             const daysDiff = Math.ceil(diff / (1000 * 3600 * 24)) + 1;
             const newStart = new Date(period.start);
             newStart.setDate(newStart.getDate() - daysDiff);
             const newEnd = new Date(period.end);
             newEnd.setDate(newEnd.getDate() - daysDiff);
             setPeriod({ start: newStart, end: newEnd });
        }
    };

    const handleNextPeriod = () => {
        const isFullMonth = period.start.getDate() === 1 &&
                           period.end.getDate() === new Date(period.start.getFullYear(), period.start.getMonth() + 1, 0).getDate();
        if (isFullMonth) {
             setPeriod({
                start: new Date(period.start.getFullYear(), period.start.getMonth() + 1, 1),
                end: new Date(period.start.getFullYear(), period.start.getMonth() + 2, 0)
             });
        } else {
             const diff = period.end.getTime() - period.start.getTime();
             const daysDiff = Math.ceil(diff / (1000 * 3600 * 24)) + 1;
             const newStart = new Date(period.start);
             newStart.setDate(newStart.getDate() + daysDiff);
             const newEnd = new Date(period.end);
             newEnd.setDate(newEnd.getDate() + daysDiff);
             setPeriod({ start: newStart, end: newEnd });
        }
    };

    const viewMode = useMemo(() => {
        const s = period.start;
        const e = period.end;
        const diffDays = Math.round((e - s) / (1000 * 3600 * 24)) + 1;
        if (diffDays === 7 && s.getDay() === 1) return 'week';
        if (s.getDate() === 1) {
            const lastOfMonth = new Date(s.getFullYear(), s.getMonth() + 1, 0);
            if (e.getDate() === lastOfMonth.getDate() && e.getMonth() === s.getMonth() && e.getFullYear() === s.getFullYear()) return 'month';
        }
        return 'custom';
    }, [period]);

    const handleGoToday = useCallback(() => {
        const now = new Date();
        setPeriod({
            start: new Date(now.getFullYear(), now.getMonth(), 1),
            end: new Date(now.getFullYear(), now.getMonth() + 1, 0),
        });
    }, []);

    const handleGoWeek = useCallback(() => {
        const now = new Date();
        const dayOfWeek = now.getDay();
        const monday = new Date(now);
        monday.setDate(now.getDate() - ((dayOfWeek + 6) % 7));
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        setPeriod({ start: monday, end: sunday });
    }, []);

    const handleGoMonth = useCallback(() => {
        const ref = period.start || new Date();
        setPeriod({
            start: new Date(ref.getFullYear(), ref.getMonth(), 1),
            end: new Date(ref.getFullYear(), ref.getMonth() + 1, 0),
        });
    }, [period.start]);

    const MAX_RANGE_DAYS = 366;

    const handleApplyCustomRange = useCallback(() => {
        if (!customStart || !customEnd) return;
        const s = new Date(customStart + 'T00:00:00');
        const e = new Date(customEnd + 'T00:00:00');
        if (isNaN(s.getTime()) || isNaN(e.getTime()) || s > e) return;
        const diffDays = Math.ceil((e - s) / (1000 * 3600 * 24)) + 1;
        if (diffDays > MAX_RANGE_DAYS) {
            alert(`La période ne peut pas dépasser ${MAX_RANGE_DAYS} jours (1 an).`);
            return;
        }
        setPeriod({ start: s, end: e });
        setIsDatePickerOpen(false);
    }, [customStart, customEnd]);

    useEffect(() => {
        setCustomStart(formatDate(period.start));
        setCustomEnd(formatDate(period.end));
    }, [period]);

    useEffect(() => {
        if (!isDatePickerOpen) return;
        const handler = (e) => {
            if (calendarPanelRef.current?.contains(e.target)) return;
            setIsDatePickerOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isDatePickerOpen]);

    // --- ReactiveJsonRoot data for TimeLogManager.yaml (modal CRUD) ---
    const todayStr = useMemo(() => formatDate(new Date()), []);

    const activeTaskId = modalData?.taskId || selectedTaskId;
    const activeProjectId = modalData?.project || selectedProjectId;

    const modalTimeLogData = useMemo(() => {
        if (!activeTaskId || !activeProjectId) return null;

        const userOpts = users
            .filter(u => u.id && u.name)
            .map(u => ({ value: String(u.id), label: u.name }));

        const collectionUrl = `/decoupled-api/node/time_log/collection?display=decoupled&filter[0][f]=field_task&filter[0][v]=${activeTaskId}`;

        const newTL = {
            submission_url: "/decoupled-api/save?display=decoupled&entity_type=node&bundle=time_log",
            data: {
                title: "Time log",
                date: modalData?.date || todayStr,
                user: currentUserId ? String(currentUserId) : "",
                description: "",
                timeSpent: "",
                timeLeft: "",
                taskId: String(activeTaskId),
                projectId: String(activeProjectId),
            },
            status: null,
        };

        return {
            timeLogs: [],
            userOptions: userOpts,
            newTimeLog: { ...newTL },
            newTimeLogTemplate: { ...newTL },
            saveTimeLogNow: false,
            taskId: String(activeTaskId),
            projectId: String(activeProjectId),
            timeLogCollectionUrl: collectionUrl,
        };
    }, [activeTaskId, activeProjectId, users, currentUserId, modalData?.date, todayStr]);

    // --- Available tasks for the project/task selector in the modal ---
    const availableModalTasks = useMemo(() => {
        if (!selectedProjectId) return [];
        return issues.filter(i => String(i.projectId) === String(selectedProjectId));
    }, [issues, selectedProjectId]);

    const activeProject = projects.find(p => String(p.id) === String(activeProjectId));
    const activeTask = issues.find(i => String(i.id) === String(activeTaskId));

    return (
        <ActionDependant {...props}>
            <div className="flex flex-col h-full bg-slate-50 text-sm">
                {/* Header */}
                <div className="px-5 py-3 flex items-center justify-between bg-white border-b border-slate-200 shadow-sm z-30 relative">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1 relative">
                             <button onClick={handlePrevPeriod} className="p-1 hover:bg-white rounded shadow-sm transition-all text-slate-600">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                             </button>
                             <span
                                className="px-2 font-semibold text-slate-700 min-w-[180px] text-center cursor-pointer hover:text-blue-600 transition-colors select-none"
                                onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                             >
                                {period.start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - {period.end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                             </span>
                             <button onClick={handleNextPeriod} className="p-1 hover:bg-white rounded shadow-sm transition-all text-slate-600">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                             </button>
                        </div>
                        <button onClick={handleGoToday} className="btn btn-secondary btn-sm">
                            Aujourd&apos;hui
                        </button>
                        <div className="flex items-center bg-white border border-slate-300 rounded-md overflow-hidden">
                            <button onClick={handleGoWeek} className={`text-xs px-2.5 py-1.5 border-r border-slate-300 transition-colors ${viewMode === 'week' ? 'bg-iris-50 text-iris-700 font-medium' : 'text-slate-600 hover:bg-iris-50 hover:text-iris-600'}`}>Sem.</button>
                            <button onClick={handleGoMonth} className={`text-xs px-2.5 py-1.5 transition-colors ${viewMode === 'month' ? 'bg-iris-50 text-iris-700 font-medium' : 'text-slate-600 hover:bg-iris-50 hover:text-iris-600'}`}>Mois</button>
                        </div>
                        <div ref={calendarPanelRef} className="relative">
                            <button
                                onClick={() => setIsDatePickerOpen(o => !o)}
                                className={`p-1.5 rounded-md border transition-colors ${isDatePickerOpen || viewMode === 'custom' ? 'bg-iris-50 border-iris-400 text-iris-600' : 'bg-white border-slate-300 text-slate-500 hover:border-iris-400 hover:text-iris-600'}`}
                                title="Calendrier"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                            </button>

                            {isDatePickerOpen && (
                                <div className="absolute top-full right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-2xl z-50" style={{width: '300px'}}>
                                    <div className={`px-4 py-3 ${customStart !== formatDate(period.start) || customEnd !== formatDate(period.end) ? 'border-blue-300 bg-blue-50 rounded-t-xl' : ''}`}>
                                        <div className="text-xs text-slate-500 font-medium mb-2">Période personnalisée</div>
                                        <div className="grid grid-cols-2 gap-2 mb-2">
                                            <div>
                                                <label className="block text-xs text-slate-500 mb-0.5">Du</label>
                                                <input
                                                    type="date"
                                                    value={customStart}
                                                    onChange={(e) => setCustomStart(e.target.value)}
                                                    className="input text-xs"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-slate-500 mb-0.5">Au</label>
                                                <input
                                                    type="date"
                                                    value={customEnd}
                                                    onChange={(e) => setCustomEnd(e.target.value)}
                                                    className="input text-xs"
                                                />
                                            </div>
                                        </div>
                                        <button
                                            onClick={handleApplyCustomRange}
                                            className="btn btn-primary btn-sm btn-block"
                                        >
                                            Appliquer la période
                                        </button>
                                        <div className="text-xs text-slate-400 text-center mt-1.5">Max. 1 an</div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="w-64 relative" ref={userDropdownRef}>
                            <button
                                type="button"
                                onClick={() => { setUserDropdownOpen(o => !o); setUserSearch(''); }}
                                className="flex items-center gap-2 w-full text-left bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                            >
                                {currentUserObj ? (
                                    <>
                                        <span className="avatar avatar-sm flex-shrink-0">
                                            {initials(currentUserObj.name)}
                                        </span>
                                        <span className="truncate flex-1 text-slate-800 font-medium">{currentUserObj.name}</span>
                                    </>
                                ) : (
                                    <span className="text-slate-400 flex-1">Sélectionner un utilisateur...</span>
                                )}
                                <svg className={`h-4 w-4 text-slate-400 flex-shrink-0 transition-transform ${userDropdownOpen ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </button>
                            {userDropdownOpen && (
                                <div className="absolute top-full left-0 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden">
                                    {users.length > 5 && (
                                        <div className="p-2 border-b border-slate-100">
                                            <input
                                                ref={userSearchInputRef}
                                                type="text"
                                                value={userSearch}
                                                onChange={(e) => setUserSearch(e.target.value)}
                                                placeholder="Rechercher..."
                                                className="w-full text-sm border border-slate-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                    )}
                                    <div className="max-h-60 overflow-y-auto">
                                        {filteredUserList.length > 0 ? (
                                            filteredUserList.map(user => (
                                                <button
                                                    key={user.id}
                                                    type="button"
                                                    onClick={() => {
                                                        setCurrentUser(user.name);
                                                        setUserDropdownOpen(false);
                                                        setUserSearch('');
                                                    }}
                                                    className={`flex items-center gap-2.5 w-full text-left px-3 py-2 text-sm transition-colors
                                                        ${user.name === currentUser ? 'bg-blue-50 text-blue-700' : 'hover:bg-slate-50 text-slate-800'}
                                                    `}
                                                >
                                                    <span className={`avatar avatar-sm flex-shrink-0
                                                        ${user.name === currentUser ? 'bg-blue-200 text-blue-800' : 'bg-slate-100 text-slate-600'}
                                                    `}>
                                                        {initials(user.name || '?')}
                                                    </span>
                                                    <span className="truncate">{user.name}</span>
                                                    {user.name === currentUser && (
                                                        <svg className="h-4 w-4 text-blue-600 ml-auto flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                                    )}
                                                </button>
                                            ))
                                        ) : (
                                            <div className="px-3 py-3 text-sm text-slate-400 italic text-center">Aucun utilisateur trouvé</div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <label className="flex items-center gap-1.5 cursor-pointer select-none" title="Masquer les colonnes samedi et dimanche">
                            <input
                                type="checkbox"
                                checked={hideWeekends}
                                onChange={(e) => setHideWeekends(e.target.checked)}
                                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                            />
                            <span className="text-xs text-slate-500">Masquer week-ends</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer select-none" title="N'afficher que les totaux par jour (masquer le détail par ticket)">
                            <input
                                type="checkbox"
                                checked={hideTaskRows}
                                onChange={(e) => setHideTaskRows(e.target.checked)}
                                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                            />
                            <span className="text-xs text-slate-500">Masquer lignes tickets</span>
                        </label>
                        <div className="text-right mr-4">
                            <div className="text-xs text-slate-500">Total période</div>
                            <div className="font-bold text-lg text-blue-600">
                                {decimalToHMLabel(periodTotal)}
                            </div>
                        </div>
                        <button
                            onClick={handleOpenNewLog}
                            className="btn btn-primary"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                            Saisir du temps
                        </button>
                    </div>
                </div>

                {/* Grid */}
                <div className="flex-1 relative p-4 min-h-0">
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-auto h-full">
                    <table className="min-w-full border-collapse">
                        <thead className="bg-slate-50 sticky top-0 z-10 shadow-sm">
                            <tr>
                                <th rowSpan={2} className="p-3 text-left font-medium text-slate-700 border-b border-slate-300 border-r border-r-slate-300 min-w-[500px] w-[500px] sticky left-0 bg-slate-100 z-20">Projet / Tâche</th>
                                {monthSpans.map((span, i) => (
                                    <th
                                        key={span.key}
                                        colSpan={span.count}
                                        className={`px-2 py-1.5 text-center text-xs font-semibold text-iris-700 bg-iris-50 border-b border-slate-300 border-r border-r-slate-300 capitalize ${i > 0 ? 'border-l-2 border-l-iris-300' : ''}`}
                                    >
                                        {span.label}
                                    </th>
                                ))}
                            </tr>
                            <tr>
                                {visibleDays.map((day, idx) => {
                                    const isToday = formatDate(day) === formatDate(new Date());
                                    const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                                    const isWeekSep = hideWeekends && day.getDay() === 1 && idx > 0;
                                    const isMonthStart = idx > 0 && day.getMonth() !== visibleDays[idx - 1].getMonth();
                                    return (
                                        <th key={day.toISOString()} className={`p-1 text-center border-b border-slate-300 border-r border-r-slate-300 min-w-[40px] w-[40px] ${isToday ? 'bg-blue-100 border-blue-300' : 'bg-slate-100'} ${isWeekend ? 'bg-slate-200' : ''} ${isWeekSep ? 'border-l-2 border-l-slate-400' : ''} ${isMonthStart && !isWeekSep ? 'border-l-2 border-l-iris-300' : ''}`}>
                                            <div className="text-xs text-slate-500 font-semibold">{day.toLocaleDateString('fr-FR', { weekday: 'short' }).slice(0, 2)}</div>
                                            <div className={`text-sm font-bold ${isToday ? 'text-blue-700' : 'text-slate-800'}`}>{day.getDate()}</div>
                                        </th>
                                    );
                                })}
                            </tr>
                            {hasLogs && (
                                <tr className="bg-slate-200 font-bold">
                                    <td className="p-2 text-right border-b border-slate-300 border-r border-r-slate-300 sticky left-0 bg-slate-200 z-20 text-slate-800 text-xs">Total</td>
                                    {visibleDays.map((day, idx) => {
                                        const dateStr = formatDate(day);
                                        const val = dailyTotals[dateStr] || 0;
                                        const isSeven = Math.abs(val - 7) < 0.01;
                                        const isWeekSep = hideWeekends && day.getDay() === 1 && idx > 0;
                                        let textColorClass = 'text-slate-800';
                                        if (val > 0) {
                                            textColorClass = isSeven ? 'text-green-600 font-bold' : 'text-red-600 font-bold';
                                        }
                                        return (
                                            <td key={dateStr} className={`p-1 text-center border-b border-r border-slate-300 text-xs ${textColorClass} ${isWeekSep ? 'border-l-2 border-l-slate-400' : ''}`}>
                                                {val > 0 ? decimalToHMLabel(val) : ''}
                                            </td>
                                        );
                                    })}
                                </tr>
                            )}
                        </thead>
                        <tbody className="bg-white">
                            {hasLogs ? (
                                (hideTaskRows ? [] : projects).map(project => {
                                const projectIssues = filteredIssues.filter(i => String(i.projectId) === String(project.id));
                                if (projectIssues.length === 0) return null;

                                const projectTotal = projectTotals[String(project.id)] || { total: 0, byDate: {} };

                                return (
                                    <React.Fragment key={project.id}>
                                        <tr className="bg-slate-100">
                                            <td className="p-2 pl-4 font-bold text-slate-800 border-b border-slate-300 sticky left-0 bg-slate-100 z-10 border-r border-r-slate-300">
                                                <div className="flex items-center gap-2">
                                                    <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0"></span>
                                                    <span className="truncate">{project.name}</span>
                                                    <span className="ml-auto flex-shrink-0 text-xs font-bold text-slate-600 tabular-nums" title="Total du projet sur la période">
                                                        {decimalToHMLabel(projectTotal.total)}
                                                    </span>
                                                </div>
                                            </td>
                                            {visibleDays.map((day, idx) => {
                                                const dateStr = formatDate(day);
                                                const val = projectTotal.byDate[dateStr] || 0;
                                                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                                                const isWeekSep = hideWeekends && day.getDay() === 1 && idx > 0;
                                                return (
                                                    <td
                                                        key={dateStr}
                                                        className={`p-1 text-center border-b border-r border-slate-300 text-xs font-bold text-slate-600 tabular-nums ${isWeekend ? 'bg-slate-200' : 'bg-slate-100'} ${isWeekSep ? 'border-l-2 border-l-slate-400' : ''}`}
                                                    >
                                                        {val > 0 ? decimalToHMLabel(val) : ''}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                        {projectIssues.map(issue => (
                                            <tr key={issue.id} className="group hover:bg-blue-50 transition-colors">
                                                <td className="p-2 pl-6 border-b border-slate-300 border-r border-r-slate-300 sticky left-0 bg-white group-hover:bg-blue-50 z-10">
                                                    <div className="flex items-center">
                                                        <a
                                                            href={`/task-edit?id=${issue.id}`}
                                                            className="flex-shrink-0 mr-2 text-slate-400 hover:text-iris-600 transition-colors"
                                                            title="Ouvrir la tâche"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                            </svg>
                                                        </a>
                                                        <div className="truncate max-w-[400px]" title={`#${issue.id} - ${issue.title}`}>
                                                            <span className="text-blue-700 font-medium mr-2">#{issue.id}</span>
                                                            <span className="text-slate-700">{issue.title}</span>
                                                        </div>
                                                        <span className="ml-auto flex-shrink-0 pl-2 text-xs font-bold text-slate-600 tabular-nums" title="Total du ticket sur la période">
                                                            {decimalToHMLabel(ticketTotals[String(issue.id)] || 0)}
                                                        </span>
                                                    </div>
                                                </td>
                                                {visibleDays.map((day, idx) => {
                                                    const dateStr = formatDate(day);
                                                    const val = logsMap[`${issue.id}-${dateStr}`];
                                                    const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                                                    const isWeekSep = hideWeekends && day.getDay() === 1 && idx > 0;

                                                    return (
                                                        <td
                                                            key={dateStr}
                                                            className={`border-b border-r border-slate-300 text-center cursor-pointer transition-colors relative
                                                                ${isWeekend ? 'bg-slate-200' : 'bg-white'}
                                                                ${isWeekSep ? 'border-l-2 border-l-slate-400' : ''}
                                                                hover:bg-blue-100
                                                            `}
                                                            title={logsTooltipMap[`${issue.id}-${dateStr}`] || undefined}
                                                            onClick={() => handleCellClick(day, project, issue)}
                                                        >
                                                            {val ? (
                                                                <span className="inline-block px-1.5 py-0.5 rounded text-xs font-bold text-blue-700 bg-blue-100 border border-blue-200">
                                                                    {decimalToHMLabel(val)}
                                                                </span>
                                                            ) : (
                                                                <div className="h-6 w-full opacity-0 hover:opacity-100 flex items-center justify-center text-slate-400 text-xs font-bold">+</div>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                );
                            })
                            ) : (
                                <tr>
                                    <td colSpan={visibleDays.length + 1} className="p-8 text-center text-slate-400 italic">
                                        Aucun log de temps sur cette période.
                                    </td>
                                </tr>
                            )}
                            {hasLogs && (
                                <tr className="bg-slate-200 font-bold sticky bottom-0 z-20 shadow-[0_-1px_3px_rgba(0,0,0,0.2)]">
                                    <td className="p-2 text-right border-t border-slate-300 border-r border-r-slate-300 sticky left-0 bg-slate-200 z-30 text-slate-800 text-xs">Total</td>
                                    {visibleDays.map((day, idx) => {
                                        const dateStr = formatDate(day);
                                        const val = dailyTotals[dateStr] || 0;
                                        const isSeven = Math.abs(val - 7) < 0.01;
                                        const isWeekSep = hideWeekends && day.getDay() === 1 && idx > 0;
                                        let textColorClass = 'text-slate-800';
                                        if (val > 0) {
                                            textColorClass = isSeven ? 'text-green-600 font-bold' : 'text-red-600 font-bold';
                                        }
                                        return (
                                            <td key={dateStr} className={`p-1 text-center border-t border-r border-slate-300 text-xs ${textColorClass} ${isWeekSep ? 'border-l-2 border-l-slate-400' : ''}`}>
                                                {val > 0 ? decimalToHMLabel(val) : ''}
                                            </td>
                                        );
                                    })}
                                </tr>
                            )}
                        </tbody>
                    </table>
                  </div>
                </div>

                {/* Modal — CRUD via ReactiveJsonRoot + TimeLogManager.yaml */}
                {isModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="timesheet-modal-title">
                        <div className="fixed inset-0 bg-slate-500 bg-opacity-75 transition-opacity" onClick={handleModalClose}></div>
                        <div className="relative bg-white rounded-lg text-left shadow-xl w-full max-w-5xl max-h-[95vh] flex flex-col">
                            <div className="px-6 pt-5 pb-4 flex-shrink-0">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="text-lg leading-6 font-medium text-slate-900" id="timesheet-modal-title">
                                            {title}
                                        </h3>
                                        {activeProject && activeTask && (
                                            <div className="mt-1 text-sm text-slate-500">
                                                {activeProject.name} — <span className="font-medium text-slate-700">#{activeTask.id} {activeTask.title}</span>
                                            </div>
                                        )}
                                    </div>
                                    <button onClick={handleModalClose} className="text-slate-400 hover:text-slate-500">
                                        <span className="sr-only">Fermer</span>
                                        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Project/Task selector (when opened without task context) */}
                                {!modalData?.taskId && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 bg-slate-50 p-4 rounded-lg border border-slate-200">
                                        <div>
                                            <label className="block text-xs font-medium text-slate-500 mb-1">Projet <span className="text-red-500">*</span></label>
                                            <select
                                                value={selectedProjectId || ''}
                                                onChange={(e) => {
                                                    setSelectedProjectId(e.target.value || null);
                                                    setSelectedTaskId(null);
                                                }}
                                                className="select"
                                            >
                                                <option value="">Sélectionner un projet...</option>
                                                {/* Le picto est une icône SVG : une <option> ne rend que du texte. */}
                                                {projects.map(p => (
                                                    <option key={p.id} value={p.id}>{p.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-slate-500 mb-1">Tâche <span className="text-red-500">*</span></label>
                                            <TaskSearchInput
                                                value={selectedTaskId}
                                                onChange={setSelectedTaskId}
                                                tasks={availableModalTasks}
                                                projects={projects}
                                                placeholder={selectedProjectId ? "Rechercher par ID ou titre..." : "Sélectionnez d'abord un projet"}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="px-6 pb-4 overflow-y-auto flex-1 min-h-0">
                                {/* TimeLogManager — DataSync-powered CRUD */}
                                {modalTimeLogData ? (
                                    <ReactiveJsonRoot
                                        key={`${modalTimeLogData.taskId}-${modalTimeLogData.projectId}`}
                                        rjBuildUrl="/components/task/TimeLogManager.yaml"
                                        dataOverride={modalTimeLogData}
                                        plugins={TIME_LOG_PLUGINS}
                                    />
                                ) : (
                                    <div className="text-center py-8 text-slate-400 text-sm">
                                        Sélectionnez un projet et une tâche pour saisir du temps.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </ActionDependant>
    );
};
